import './rechnungen.css';
import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { fmtDate } from '../../lib/format';
import { eurC, parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { RechnungSchreiben } from '../abrechnung/RechnungSchreiben';
import { useKanzlei } from '../context';
import type { InvoicesTabResponse, KanzleiInvoice } from './types';

// Reiter „Rechnungen“ beim Mandanten (wie in der Demo, Admin und FiBu): alle Rechnungen der Kanzlei an den Mandanten,
// offene oben, mit „Als bezahlt setzen“ („Doch offen“ nimmt es zurück), „Stornieren“ (Stornorechnung) und „Löschen“,
// solange der Mandant die Rechnung nicht geöffnet hat (alles mit Rückfrage).
// Hochladen in zwei Schritten: Angaben und PDF, dann prüfen und „Ja, senden“. Der Admin kann die Rechnung auch direkt
// in der App schreiben („Rechnung schreiben“, wie unter „Abrechnung“).

type Draft = { number: string; amount: string; service: string; invoiceDate: string; dueDate: string };
type Stage = { number: string; amount: number; service: string; invoiceDate: string; dueDate: string; file: File };
type Action = 'bezahlt' | 'storno' | 'loeschen';

// 'YYYY-MM-DD' plus Tage
const addDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const isPdf = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);

export function RechnungenTab({ clientId, contact }: { clientId: string; contact: string }) {
  const toast = useToast();
  const admin = useKanzlei().me.user.isAdmin;
  const [write, setWrite] = useState(false);
  const res = useApi<InvoicesTabResponse>(`/api/kanzlei/invoices/${clientId}`, { live: ['invoices', 'clients'], clientId });
  const [ask, setAsk] = useState<{ id: string; a: Action } | null>(null);
  const [draft, setDraft] = useState<Partial<Draft>>({});
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [stage, setStage] = useState<Stage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const d = res.data;
  if (!d) return res.error ? <p className="note">{res.error}</p> : <div className="spinner" aria-label="Lädt" />;

  const f: Draft = {
    number: draft.number ?? d.nextNumber,
    amount: draft.amount ?? '',
    service: draft.service ?? '',
    invoiceDate: draft.invoiceDate ?? d.today,
    dueDate: draft.dueDate ?? addDays(d.today, 14),
  };
  const set = (v: Partial<Draft>) => {
    setDraft((x) => ({ ...x, ...v }));
    setError(null);
  };

  // Schritt 1: Angaben prüfen, dann die Rückfrage zeigen
  const check = (e: FormEvent) => {
    e.preventDefault();
    const amount = parseEuro(f.amount);
    if (!f.number.trim()) return setError('Bitte die Rechnungsnummer eintragen.');
    if (!amount || amount <= 0) return setError('Bitte den Betrag prüfen, z. B. 476,50.');
    if (!f.service.trim()) return setError('Bitte die Leistung eintragen.');
    if (d.items.some((x) => x.number === f.number.trim())) return setError('Diese Rechnungsnummer gibt es schon.');
    if (!f.invoiceDate) return setError('Bitte das Rechnungsdatum prüfen.');
    if (f.invoiceDate > d.today) return setError('Das Rechnungsdatum liegt in der Zukunft. Bitte prüfen.');
    if (!f.dueDate) return setError('Bitte das Fälligkeitsdatum angeben.');
    if (f.dueDate < f.invoiceDate) return setError('Die Fälligkeit liegt vor dem Rechnungsdatum. Bitte prüfen.');
    if (!file) return setError('Bitte die Rechnung als PDF wählen.');
    if (!isPdf(file)) return setError('Das ist keine PDF. Bitte die Rechnung als PDF hochladen.');
    setError(null);
    setStage({ ...f, number: f.number.trim(), service: f.service.trim(), amount, file });
  };

  // Schritt 2: „Ja, senden“
  const send = async (s: Stage) => {
    setBusy(true);
    const fd = new FormData();
    // zuerst die Angaben, dann die Datei (so prüft der Server, bevor er speichert)
    fd.append(
      'data',
      JSON.stringify({
        number: s.number,
        service: s.service,
        amount: s.amount,
        invoiceDate: s.invoiceDate,
        dueDate: s.dueDate,
      }),
    );
    fd.append('file', s.file, s.file.name);
    try {
      await api.post(`/api/kanzlei/invoices/${clientId}`, fd);
      toast(`Rechnung ${s.number} hochgeladen · ${d.client.name} sieht sie in der App`);
      setStage(null);
      setDraft({});
      setFile(null);
      setFileKey((x) => x + 1);
      await res.reload();
    } catch (err) {
      setStage(null);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (x: KanzleiInvoice, a: Action | 'offen') => {
    setBusy(true);
    try {
      let done: string;
      if (a === 'storno') {
        const r = await api.post<{ number: string }>(`/api/kanzlei/invoice/${x.id}/storno`, {});
        done = `${x.number} storniert · Stornorechnung ${r.number} ist in der App`;
      } else if (a === 'loeschen') {
        await api.del(`/api/kanzlei/invoice/${x.id}`);
        done = `${x.number} gelöscht`;
      } else {
        await api.post(`/api/kanzlei/invoice/${x.id}/status`, { status: a });
        done = `${x.number} ${a === 'bezahlt' ? 'als bezahlt gesetzt' : 'ist wieder offen'} · sichtbar in der App`;
      }
      setAsk(null);
      await res.reload();
      toast(done);
    } catch (err) {
      setAsk(null);
      toast(errorText(err));
      await res.reload();
    } finally {
      setBusy(false);
    }
  };

  const askText = (a: Action) =>
    a === 'bezahlt'
      ? `Als bezahlt setzen (am ${fmtDate(d.today)})?`
      : a === 'storno'
        ? `Rechnung stornieren? Die App schreibt eine Stornorechnung mit eigener Nummer, ${contact} bekommt sie in der App und muss die Rechnung nicht zahlen.`
        : `Rechnung löschen? ${contact} hat sie noch nicht geöffnet. Sie verschwindet ganz, die Nummer wird wieder frei.`;

  return (
    <div className="re-k">
      <div className="card">
        <div className="ch">
          <h3>Rechnungen an {d.client.name}</h3>
          <span>
            Offen: <b>{eurC(d.open)}</b>
          </span>
          {admin && (
            <button type="button" className="btn sm accent re-write" onClick={() => setWrite(true)}>
              Rechnung schreiben
            </button>
          )}
        </div>
        <div className="tw">
          <table className="tre">
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Leistung</th>
                <th>Fällig</th>
                <th className="num">Betrag</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="note">
                    Noch keine Rechnungen.
                  </td>
                </tr>
              ) : (
                d.items.map((x) => (
                  <tr key={x.id} className={x.status === 'offen' || x.status === 'bezahlt' ? undefined : 're-x'}>
                    <td className="re-nr">
                      <b>{x.number}</b>
                      <span className="sub">vom {fmtDate(x.invoiceDate)}</span>
                    </td>
                    <td data-l="Leistung">
                      {x.service}
                      <span className="sub re-file">
                        <a href={`/api/kanzlei/invoice/${x.id}/file`} target="_blank" rel="noopener noreferrer">
                          📄 {x.file.name}
                        </a>
                      </span>
                    </td>
                    <td className="num" data-l="Fällig">
                      {x.status === 'storno' ? '–' : fmtDate(x.dueDate)}
                    </td>
                    <td className="num" data-l="Betrag">
                      {eurC(x.amount)}
                    </td>
                    <td data-l="Status">
                      <StatusPill x={x} />
                    </td>
                    <td className="re-act">
                      {ask?.id === x.id ? (
                        <div className="confirmup re-ask">
                          <span className="note">{askText(ask.a)}</span>
                          <div className="re-btns re-start">
                            <button type="button" className="btn sm dark" disabled={busy} onClick={() => act(x, ask.a)}>
                              {ask.a === 'bezahlt' ? 'Ja, bezahlt' : ask.a === 'storno' ? 'Ja, stornieren' : 'Ja, löschen'}
                            </button>
                            <button type="button" className="btn sm" onClick={() => setAsk(null)}>
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      ) : x.status === 'offen' ? (
                        <div className="re-btns">
                          <button type="button" className="btn sm" onClick={() => setAsk({ id: x.id, a: 'bezahlt' })}>
                            Als bezahlt setzen
                          </button>
                          <button type="button" className="btn sm" onClick={() => setAsk({ id: x.id, a: 'storno' })}>
                            Stornieren
                          </button>
                          {x.deletable && (
                            <button type="button" className="btn sm" onClick={() => setAsk({ id: x.id, a: 'loeschen' })}>
                              Löschen
                            </button>
                          )}
                        </div>
                      ) : x.status === 'bezahlt' || x.status === 'zurueckgezogen' ? (
                        <button type="button" className="btn sm" disabled={busy} onClick={() => act(x, 'offen')}>
                          Doch offen
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="note re-foot">
          Der Mandant sieht alle Rechnungen in seiner App unter „Rechnungen“, offene oben. Löschen geht, bis er die Rechnung
          geöffnet hat, danach nur noch stornieren.
        </p>
      </div>
      {write && <RechnungSchreiben clientId={clientId} clientName={d.client.name} onClose={() => setWrite(false)} />}

      <div className="card re-up">
        <div className="ch">
          <h3>Rechnung hochladen</h3>
          <span>{stage ? 'bitte prüfen' : 'erscheint nach Ihrer Bestätigung beim Mandanten'}</span>
        </div>
        {stage ? (
          <div className="confirmup">
            <b>Rechnung an {contact} senden?</b>
            <div className="note">
              {stage.number} · {stage.service}
            </div>
            <div>
              Betrag <b>{eurC(stage.amount)}</b> · Rechnungsdatum {fmtDate(stage.invoiceDate)} · fällig am{' '}
              {fmtDate(stage.dueDate)}
            </div>
            <div className="re-file">📄 {stage.file.name}</div>
            <p className="note re-m0">
              Stimmt der Betrag? Danach sieht {contact} die Rechnung mit PDF in der App und bekommt eine Benachrichtigung.
            </p>
            <div className="re-btns re-start">
              <button type="button" className="btn sm dark" disabled={busy} onClick={() => send(stage)}>
                Ja, senden
              </button>
              <button type="button" className="btn sm" disabled={busy} onClick={() => setStage(null)}>
                Ändern
              </button>
            </div>
          </div>
        ) : (
          <form className="form" onSubmit={check} noValidate>
            <div className="row re-two">
              <label>
                Rechnungsnummer *
                <input value={f.number} maxLength={40} onChange={(e) => set({ number: e.target.value })} />
              </label>
              <label>
                Betrag brutto (€) *
                <input
                  value={f.amount}
                  inputMode="decimal"
                  placeholder="476,50"
                  onChange={(e) => set({ amount: e.target.value })}
                />
              </label>
            </div>
            <label>
              Leistung *
              <input
                value={f.service}
                maxLength={120}
                placeholder="z. B. Buchhaltung und Lohn Oktober 2026"
                onChange={(e) => set({ service: e.target.value })}
              />
            </label>
            <div className="row re-two">
              <label>
                Rechnungsdatum
                <input type="date" value={f.invoiceDate} max={d.today} onChange={(e) => set({ invoiceDate: e.target.value })} />
              </label>
              <label>
                Fällig am *
                <input type="date" value={f.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
              </label>
            </div>
            <label>
              Rechnung als PDF *
              <input
                key={fileKey}
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setError(null);
                }}
              />
            </label>
            {error && (
              <p className="note re-err" role="alert">
                {error}
              </p>
            )}
            <div>
              <button type="submit" className="btn dark">
                Weiter zur Prüfung
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function StatusPill({ x }: { x: KanzleiInvoice }) {
  switch (x.state) {
    case 'bezahlt':
      return (
        <>
          <span className="pill p-ok">bezahlt</span>
          {x.paidOn && <span className="sub">am {fmtDate(x.paidOn)}</span>}
        </>
      );
    case 'zurueckgezogen':
      return <span className="pill p-neu">zurückgezogen</span>;
    case 'storniert':
      return (
        <>
          <span className="pill p-neu">storniert</span>
          {x.storno && <span className="sub">Storno {x.storno.number}</span>}
        </>
      );
    case 'storno':
      return (
        <>
          <span className="pill p-neu">Stornorechnung</span>
          {x.stornoOf && <span className="sub">zu {x.stornoOf}</span>}
        </>
      );
    case 'ueberfaellig':
      return <span className="pill p-bad">überfällig</span>;
    default:
      return <span className="pill p-warn">offen</span>;
  }
}
